"use client";
import { useCallback, useSyncExternalStore } from "react";
import en from "./en.json";

// Traduction de l'INTERFACE, côté navigateur uniquement (étape 1 — cf.
// docs/glossaire-en.md). Le français est la clé : `t("Catalogue")` rend
// "Catalog" en anglais, et "Catalogue" partout ailleurs. Une traduction
// manquante retombe donc sur le français, jamais sur une clé technique.
//
// ⛔ La langue ne se DÉDUIT jamais (navigateur, IP, en-tête) : elle ne vient
// que d'un clic explicite mémorisé. Le HTML rendu par le serveur est toujours
// français, donc Googlebot — qui arrive sans mémoire et ne clique pas — lit le
// site français sur les URLs canoniques. C'est ce qui rend la traduction sans
// risque pour le référencement en cours.

export type Lang = "fr" | "en";

const KEY = "atlanx-lang";

const dict = en as Record<string, string>;

// La langue vit dans localStorage, pas dans un state React : useSyncExternalStore
// la lit sans setState dans un effect, et sert "fr" au serveur comme au premier
// rendu client (l'hydratation reste donc identique au HTML servi).
const listeners = new Set<() => void>();

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

function getSnapshot(): Lang {
  try {
    return localStorage.getItem(KEY) === "en" ? "en" : "fr";
  } catch {
    return "fr";
  }
}

// Rendu serveur et hydratation : français, sans exception ni condition.
function getServerSnapshot(): Lang {
  return "fr";
}

export function setLang(next: Lang) {
  try {
    localStorage.setItem(KEY, next);
  } catch {}
  // Pour les lecteurs d'écran et la césure. Invisible de Googlebot, qui n'a
  // jamais de choix mémorisé (le script d'amorce du layout fait de même au
  // rechargement).
  document.documentElement.lang = next;
  for (const fn of listeners) fn();
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export type Translate = (fr: string, vars?: Record<string, string | number>) => string;

export function useT(): Translate {
  const lang = useLang();
  return useCallback(
    (fr, vars) => {
      const s = lang === "en" ? (dict[fr] ?? fr) : fr;
      return vars ? s.replace(/\{(\w+)\}/g, (m, k) => String(vars[k] ?? m)) : s;
    },
    [lang],
  );
}
